import { Head } from '@based/sdk/react'
import { useCanonicalUrl } from '@app/util/hooks/useCanonicalUrl'
import { expandImage, type StoredImage } from '@app/types/StoredImage'
import { formatMetaDescription } from '@app/util/seo/formatMetaDescription'
import { useTenantContext } from '@app/util/contexts/TenantContext'
import { JsonLd } from './JsonLd'
import appleTouchIcon from '@app/assets/appleTouchIcon.png'
import { buildDocumentTitle } from '@app/util/seo/buildDocumentTitle'
import type { SeoMetaFields } from '@app/util/seo/seoMeta'

const OG_LOCALE = 'nl_NL'

export type PageSeoProps = {
  pageTitle?: string
  tabTitle?: string
  description?: string
  meta?: SeoMetaFields
  image?: string | StoredImage
  type?: 'website' | 'article'
  noindex?: boolean
  useSiteDefaultImage?: boolean
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

export const PageSeo = ({
  pageTitle,
  tabTitle,
  description,
  meta,
  image,
  type = 'website',
  noindex = false,
  useSiteDefaultImage = false,
  jsonLd,
}: PageSeoProps) => {
  const { title: siteTitle } = useTenantContext()
  const canonicalUrl = useCanonicalUrl()

  const documentTitle = buildDocumentTitle({
    metaTitle: meta?.metaTitle,
    pageTitle,
    tabTitle,
    siteTitle,
  })

  const metaDescription = formatMetaDescription(
    meta?.metaDescription || description,
  )

  const imageAlt = meta?.metaTitle?.trim() || pageTitle || siteTitle

  const expandedImage = expandImage(image, imageAlt)
  const imageSrc =
    expandedImage.src ?? (useSiteDefaultImage ? appleTouchIcon : undefined)

  return (
    <Head>
      <title>{documentTitle}</title>
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {metaDescription && <meta name="description" content={metaDescription} />}
      <meta property="og:title" content={documentTitle} />
      {metaDescription && (
        <meta property="og:description" content={metaDescription} />
      )}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content={OG_LOCALE} />
      {imageSrc && <meta property="og:image" content={imageSrc} />}
      <meta
        name="twitter:card"
        content={imageSrc ? 'summary_large_image' : 'summary'}
      />
      <meta name="twitter:title" content={documentTitle} />
      {metaDescription && (
        <meta name="twitter:description" content={metaDescription} />
      )}
      {imageSrc && <meta name="twitter:image" content={imageSrc} />}
      {jsonLd && <JsonLd data={jsonLd} />}
    </Head>
  )
}
